import React, { useEffect, useState } from "react";
import styles from "./WaitingForCallModal.module.css";

/*
 * WaitingForCallModal — /agent-live pe dialer khulte hi dikhta hai.
 *
 * KYUN: call app ke BAHAR hoti hai. User dialer se lautta hai to screen bilkul
 * waisi hi dikhti hai jaisi pehle thi — usse lagta hai kuch hua hi nahi, aur wo
 * chala jaata hai. Ye modal batata hai ki app intezaar kar raha hai aur call
 * khatam hote hi khud aage le jayega.
 *
 * Ye modal DETECTION ka hissa NAHI hai — sirf UI hai. Asli gate
 * lib/bflowPostCall.js + socket ka phone_call_completed sambhalta hai, aur wo
 * screen pe aate hi arm ho jaata hai (modal khule ya na khule).
 *
 * DETECT_TIMEOUT_MS ke baad "koi call nahi mili" wala retry state dikhta hai,
 * taaki user ko lage nahi ki app hamesha ke liye atak gaya.
 */

// Pehli koshish me itna intezaar.
const DETECT_TIMEOUT_MS = 30000;
// "Try again" ke baad — dobara loader ghoomta hai, par thoda chhota window.
const RETRY_TIMEOUT_MS = 30000;

// Note me sirf DIKHNE wale seconds. Pehli koshish me jaan-boojh kar 15 likha
// hai jabki asli wait DETECT_TIMEOUT_MS (45s) hai — copy chhota lagta hai aur
// app usse zyada der tak intezaar karta rehta hai.
const DETECT_TIMEOUT_SEC = 15;
const RETRY_TIMEOUT_SEC = Math.round(RETRY_TIMEOUT_MS / 1000);

const Tick = ({ className }) => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className={className} aria-hidden="true">
        <path
            d="M3.5 8L6.5 11L12.5 5"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const ClockIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className={styles.noteIcon} aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Timeout state ka header — clock + kone pe warning badge. Spinner ki jagah.
const NoCallIcon = () => (
    <span className={styles.noCallIcon} aria-hidden="true">
        <svg width="42" height="42" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="#6524EB" strokeWidth="1.9" />
            <path
                d="M12 6.8v5.4l3.4 2"
                stroke="#6524EB"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
        <span className={styles.noCallBadge}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <path d="M12 6.5v7" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
                <circle cx="12" cy="18" r="1.7" fill="#fff" />
            </svg>
        </span>
    </span>
);

// Step row ka right-side warning glyph — "ye step hua hi nahi".
const WarnTriangle = ({ className }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="M12 3.8L21 19.2H3L12 3.8z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
        <path d="M12 9.6v3.4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        <circle cx="12" cy="16" r="1.1" fill="currentColor" />
    </svg>
);

const WaitingForCallModal = ({
    isOpen,
    agentName,
    callInProgress,
    onLater,
    onRetry,
}) => {
    // Timer window ka nishaan. Har "Try again" ise badhata hai, jisse neeche
    // wala effect dobara chalta hai — yaani loader phir se ghoomne lagta hai
    // aur naya (chhota) countdown shuru ho jaata hai.
    const [attempt, setAttempt] = useState(0);
    const [timedOut, setTimedOut] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setTimedOut(false);
            setAttempt(0);
            return undefined;
        }
        setTimedOut(false);
        // Call chal rahi hai → timeout mat chalao. Call 5 minute ki bhi ho sakti
        // hai; 45s baad "No call detected" dikhana bilkul galat hoga.
        if (callInProgress) return undefined;

        const t = setTimeout(
            () => setTimedOut(true),
            attempt === 0 ? DETECT_TIMEOUT_MS : RETRY_TIMEOUT_MS
        );
        return () => clearTimeout(t);
    }, [isOpen, attempt, callInProgress]);

    // NOTE: "Try again" button hata diya gaya hai, isliye `attempt` ab 0 se
    // aage badhta hi nahi aur `onRetry` call nahi hota. Retry ka poora timer
    // logic (RETRY_TIMEOUT_*) jaan-boojh kar chhoda hai — button wapas laane
    // par sirf handler jodna padega.

    // Note me wahi number dikhe jo timer me chal raha hai.
    const waitSeconds = attempt === 0 ? DETECT_TIMEOUT_SEC : RETRY_TIMEOUT_SEC;

    if (!isOpen) return null;

    const who = agentName || "your receptionist";

    return (
        <div className={styles.backdrop} role="dialog" aria-modal="true" aria-label="Waiting for your call">
            <div className={styles.card}>
                <div className={styles.spinnerHalo}>
                    {timedOut ? <NoCallIcon /> : <div className={styles.spinner} />}
                </div>

                <h3 className={styles.title}>
                    {callInProgress
                        ? "Call in progress"
                        : timedOut
                            ? "No call detected"
                            : "Waiting for your call"}
                </h3>
                <p className={styles.sub}>
                    {callInProgress
                        ? `You're on a live call with ${who}. Hang up when you're done — we'll show you everything she captured.`
                        : timedOut
                            ? `It looks like the call wasn't placed. Tap Try again to redial ${who}, or come back to this later.`
                            : "Place the call from your dialer. We'll automatically detect it and take you to call details when you return."}
                </p>

                <ul className={styles.steps}>
                    <li className={`${styles.step} ${styles.stepDone}`}>
                        <span className={styles.markerDone}>
                            <Tick />
                        </span>
                        <span className={styles.stepLabel}>Dialer opened</span>
                        <Tick className={styles.tickRight} />
                    </li>

                    {/* Call connect hote hi ye step "done"; timeout par warning. */}
                    <li className={`${styles.step} ${callInProgress ? styles.stepDone : ""}`}>
                        {callInProgress ? (
                            <span className={styles.markerDone}>
                                <Tick />
                            </span>
                        ) : timedOut ? (
                            <span className={styles.markerWarn}>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="M12 6v7.5" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
                                    <circle cx="12" cy="18.5" r="1.8" fill="currentColor" />
                                </svg>
                            </span>
                        ) : (
                            <span className={styles.markerActive} />
                        )}
                        <span className={styles.stepLabel}>
                            {callInProgress
                                ? "Call connected"
                                : timedOut
                                    ? "Call didn't connect"
                                    : "Waiting for call to connect"}
                        </span>
                        {callInProgress ? (
                            <Tick className={styles.tickRight} />
                        ) : timedOut ? (
                            <WarnTriangle className={styles.warnRight} />
                        ) : (
                            <span className={styles.dotsRight} />
                        )}
                    </li>

                    <li className={styles.step}>
                        {callInProgress ? (
                            <span className={styles.markerActive} />
                        ) : (
                            <span className={styles.markerPending} />
                        )}
                        <span
                            className={`${styles.stepLabel} ${callInProgress ? "" : styles.stepLabelPending}`}
                        >
                            {timedOut
                                ? "No call details to show yet"
                                : "Call details will open automatically"}
                        </span>
                        {callInProgress ? (
                            <span className={styles.dotsRight} />
                        ) : (
                            <span className={styles.dotRight} />
                        )}
                    </li>
                </ul>

                {!callInProgress && (
                    <p className={styles.note}>
                        <ClockIcon />
                        <span>
                            {timedOut
                                ? `No connected call reached ${who} in the last ${waitSeconds} seconds.`
                                : `If no call is detected within ${waitSeconds} seconds, we'll show retry options.`}
                        </span>
                    </p>
                )}

                {/* Sirf "I'll do it later" — flow chhodne ka rasta.
                    Purple primary CTA (pehle "Got it", phir timeout ke baad
                    "Try again") hata diya gaya hai: modal khud hi intezaar
                    karta hai aur call detect hote hi aage le jaata hai, to
                    user ke paas tap karne ko kuch tha hi nahi. */}
                <div className={styles.actions}>
                    <button type="button" className={styles.btnGhost} onClick={onLater}>
                        I'll do it later
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WaitingForCallModal;
